// MOCK: sync telemetry. Replace with real job status; keep the SyncStatus shape.
/**
 * Sync status for the report header.
 *
 * The two timestamps are deliberately distinct and must never be used
 * interchangeably in copy:
 *   lastSyncAt  — when the ingestion job last ran
 *   dataThrough — the last COMPLETE day of data it produced
 * Customers conflate them and conclude the product is a day behind when it is
 * working correctly, so the popover always shows both.
 */

export type SyncFailureCause = "TOKEN_REVOKED" | "PERMISSION_REMOVED" | "QUOTA_EXCEEDED" | "PROPERTY_DELETED";

export type SyncHealth = "HEALTHY" | "STALE" | "FAILED";

/** Syncs run once every 24 hours. Not configurable, not manually triggerable. */
export const SYNC_INTERVAL_HOURS = 24;
/** Past this, the SYNCING copy softens rather than erroring. */
export const SLOW_SYNC_MINUTES = 45;
/** Google finalises data over this window, so recent days keep moving. */
export const FINALISATION_DAYS = 2;
/** Older than this and we must say so rather than serve the numbers quietly. */
export const STALE_AFTER_HOURS = 48;

export interface SyncStatus {
    health: SyncHealth;
    lastSyncAt: Date;
    nextSyncAt: Date;
    /** Last complete day of data — always earlier than lastSyncAt. */
    dataThrough: Date;
    failureCause: SyncFailureCause | null;
}

function atHour(base: Date, dayOffset: number, hours: number, minutes: number): Date {
    const d = new Date(base);
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hours, minutes, 0, 0);
    return d;
}

/**
 * A plausible, stable status. Times hang off "today" so the report never looks
 * abandoned, but the clock positions are fixed so screenshots stay comparable.
 */
export function buildSyncStatus(health: SyncHealth, failureCause: SyncFailureCause | null = null): SyncStatus {
    const now = new Date();

    if (health === "HEALTHY") {
        return {
            health,
            lastSyncAt: atHour(now, 0, 4, 12),
            nextSyncAt: atHour(now, 1, 4, 12),
            dataThrough: atHour(now, -1, 23, 59),
            failureCause: null
        };
    }

    // Stale and failed both mean the last good run was days ago; the difference
    // is whether we know why.
    const lastGood = atHour(now, -3, 4, 12);
    return {
        health,
        lastSyncAt: lastGood,
        nextSyncAt: atHour(now, 1, 4, 12),
        dataThrough: atHour(now, -4, 23, 59),
        failureCause: health === "FAILED" ? failureCause ?? "TOKEN_REVOKED" : null
    };
}

/** MOCK: dev-only switcher options for §7. */
export type DevSyncState =
    | "HEALTHY"
    | "SYNCING"
    | "SYNCING_SLOW"
    | "STALE"
    | "FAILED_TOKEN"
    | "FAILED_PERMISSION"
    | "FAILED_QUOTA"
    | "FAILED_DELETED";

export const DEV_SYNC_STATES: { value: DevSyncState; label: string }[] = [
    { value: "HEALTHY", label: "Healthy" },
    { value: "SYNCING", label: "Syncing" },
    { value: "SYNCING_SLOW", label: "Syncing (slow)" },
    { value: "STALE", label: "Stale" },
    { value: "FAILED_TOKEN", label: "Failed — token revoked" },
    { value: "FAILED_PERMISSION", label: "Failed — permission removed" },
    { value: "FAILED_QUOTA", label: "Failed — quota exceeded" },
    { value: "FAILED_DELETED", label: "Failed — property deleted" }
];

const FAILURE_BY_DEV_STATE: Partial<Record<DevSyncState, SyncFailureCause>> = {
    FAILED_TOKEN: "TOKEN_REVOKED",
    FAILED_PERMISSION: "PERMISSION_REMOVED",
    FAILED_QUOTA: "QUOTA_EXCEEDED",
    FAILED_DELETED: "PROPERTY_DELETED"
};

export function syncStatusForDevState(state: DevSyncState): SyncStatus {
    if (state === "STALE") return buildSyncStatus("STALE");
    const failure = FAILURE_BY_DEV_STATE[state];
    if (failure) return buildSyncStatus("FAILED", failure);
    return buildSyncStatus("HEALTHY");
}

export function isSyncingDevState(state: DevSyncState): boolean {
    return state === "SYNCING" || state === "SYNCING_SLOW";
}

// ── Formatting ───────────────────────────────────────────────────────────────

/** Always the viewer's timezone — the property's zone is shown only in the picker. */
export function formatDateTime(d: Date): string {
    return d.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

export function formatDate(d: Date): string {
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function hoursBetween(a: Date, b: Date): number {
    return Math.abs(a.getTime() - b.getTime()) / 36e5;
}

/** "6 hours ago", "2 days ago" — coarse on purpose; precision here is noise. */
export function relativeTime(d: Date, now = new Date()): string {
    const minutes = Math.round((now.getTime() - d.getTime()) / 6e4);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** "in 18 hours" — used for the next scheduled run. */
export function relativeFuture(d: Date, now = new Date()): string {
    const minutes = Math.max(0, Math.round((d.getTime() - now.getTime()) / 6e4));
    if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
    const days = Math.round(hours / 24);
    return `in ${days} day${days === 1 ? "" : "s"}`;
}
